import { useCallback, useEffect, useState } from "react";
import firebase from "../../configs/FirebaseConfig";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { message } from "antd";
import i18n from "i18next";

const db = firebase.firestore();

const collectionName = "productMaterialGroups";

/**
 * Hook to fetch and manage product material groups from Firestore
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoFetch - Whether to automatically fetch data on mount (default: true)
 * @param {boolean} options.realtime - Whether to use real-time listener (default: true)
 * @param {boolean} options.activeOnly - Fetch only active groups (default: false)
 * @returns {Object} - Material groups data and utility functions
 */
export const useProductMaterialGroups = (options = {}) => {
  const { autoFetch = true, realtime = true, activeOnly = false } = options;
  const auth = useSelector((state) => state.firebaseReducer.auth);

  const [materialGroups, setMaterialGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all product material groups
   * @returns {Promise<Array>} - Array of material group documents
   */
  const fetchMaterialGroups = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let query = db.collection(collectionName).orderBy("sortOrder", "asc");

      if (activeOnly) {
        query = query.where("active", "==", true);
      }

      const snapshot = await query.get();

      if (snapshot.empty) {
        setMaterialGroups([]);
        return [];
      }

      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setMaterialGroups(data);
      return data;
    } catch (err) {
      console.error("Error fetching product material groups:", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [activeOnly]);

  /**
   * Add a new product material group
   * @param {Object} groupData - Group data to add
   * @param {string} groupData.name - Group name
   * @param {string} groupData.code - Group code
   * @param {number} groupData.sortOrder - Sort order (optional)
   * @returns {Promise<Object>} - Created group document
   */
  const addMaterialGroup = useCallback(
    async (groupData) => {
      if (!auth.email) {
        throw new Error("User not authenticated");
      }

      setIsLoading(true);
      setError(null);

      try {
        const newGroup = {
          ...groupData,
          sortOrder: groupData.sortOrder !== undefined ? groupData.sortOrder : 999,
          active: groupData.active !== undefined ? groupData.active : true,
          createdAt: dayjs().unix(),
          createdBy: auth.email,
          updatedAt: dayjs().unix(),
          updatedBy: auth.email,
        };

        const docRef = await db.collection(collectionName).add(newGroup);
        const createdGroup = { id: docRef.id, ...newGroup };

        message.success(i18n.t("notifications.material_group_added"));
        return createdGroup;
      } catch (err) {
        console.error("Error adding product material group:", err);
        setError(err);
        message.error(i18n.t("notifications.upload_failed"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [auth.email],
  );

  /**
   * Update an existing product material group
   * @param {string} groupId - Group document ID
   * @param {Object} groupData - Updated group data
   * @returns {Promise<Object>} - Updated group document
   */
  const updateMaterialGroup = useCallback(
    async (groupId, groupData) => {
      if (!auth.email) {
        throw new Error("User not authenticated");
      }

      setIsLoading(true);
      setError(null);

      try {
        const groupRef = db.collection(collectionName).doc(groupId);
        const groupDoc = await groupRef.get();

        if (!groupDoc.exists) {
          throw new Error("Material group not found");
        }

        const existingData = groupDoc.data();

        const updatedData = {
          ...groupData,
          updatedAt: dayjs().unix(),
          updatedBy: auth.email,
        };

        await groupRef.update(updatedData);

        const updatedGroup = { id: groupId, ...existingData, ...updatedData };
        message.success(i18n.t("notifications.material_group_updated"));
        return updatedGroup;
      } catch (err) {
        console.error("Error updating product material group:", err);
        setError(err);
        message.error(i18n.t("notifications.upload_failed"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [auth.email],
  );

  /**
   * Delete a product material group
   * @param {string} groupId - Group document ID
   * @returns {Promise<void>}
   */
  const deleteMaterialGroup = useCallback(
    async (groupId) => {
      if (!auth.email) {
        throw new Error("User not authenticated");
      }

      setIsLoading(true);
      setError(null);

      try {
        const groupRef = db.collection(collectionName).doc(groupId);
        const groupDoc = await groupRef.get();

        if (!groupDoc.exists) {
          throw new Error("Material group not found");
        }

        await groupRef.delete();
        message.success(i18n.t("notifications.material_group_deleted"));
      } catch (err) {
        console.error("Error deleting product material group:", err);
        setError(err);
        message.error(i18n.t("notifications.delete_failed"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [auth.email],
  );

  // Set up real-time listener or fetch once
  useEffect(() => {
    if (!autoFetch) return;

    if (auth.isEmpty) {
      setMaterialGroups([]);
      return;
    }

    if (realtime) {
      setIsLoading(true);
      let query = db.collection(collectionName).orderBy("sortOrder", "asc");

      if (activeOnly) {
        query = query.where("active", "==", true);
      }

      const unsubscribe = query.onSnapshot(
        (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setMaterialGroups(data);
          setIsLoading(false);
          setError(null);
        },
        (err) => {
          console.error("useProductMaterialGroups: Error in listener:", err);
          setError(err);
          setMaterialGroups([]);
          setIsLoading(false);
        },
      );

      return () => {
        if (unsubscribe) unsubscribe();
      };
    } else {
      fetchMaterialGroups();
    }
  }, [autoFetch, realtime, activeOnly, fetchMaterialGroups, auth.isEmpty]);

  return {
    materialGroups,
    isLoading,
    error,
    fetchMaterialGroups,
    addMaterialGroup,
    updateMaterialGroup,
    deleteMaterialGroup,
  };
};
